import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearSearchResults,
  selectSearchMembers,
  selectSearchSessions,
  selectSearchStatus,
} from "../../features/search/search-slice";
import { fetchSearchResults } from "../../features/search/slice-extensions/search-slice-extension";
import { parseDateQuery, toDateParam } from "../../features/search/utils/parseDateQuery";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { ROUTES } from "../../routes";

const sessionDetailsRoute = (id: string) => ROUTES.SESSION_DETAILS.replace(":id", id);
const memberDetailsRoute = (id: string) => ROUTES.MEMBER_DETAILS.replace(":id", id);

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

interface Suggestion {
  key: string;
  kind: "date" | "session" | "member";
  label: string;
  sublabel?: string;
  to: string;
}

/**
 * Header search: type to get session / member / date suggestions (fuzzy-matched
 * server-side), navigate with arrow keys + Enter or a click.
 */
const GlobalSearchBar = () => {
  const { t, i18n } = useTranslation();
  // Unique per instance — the bar can mount twice (desktop bar + mobile row).
  const listboxId = useId();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const sessions = useAppSelector(selectSearchSessions);
  const members = useAppSelector(selectSearchMembers);
  const status = useAppSelector(selectSearchStatus);

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmedQuery = query.trim();
  const isSearchable = trimmedQuery.length >= MIN_QUERY_LENGTH;

  // Fetch once the user pauses typing; short queries just clear old results.
  useEffect(() => {
    if (!isSearchable) {
      dispatch(clearSearchResults());
      return;
    }
    const handle = setTimeout(() => {
      dispatch(fetchSearchResults(trimmedQuery));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [dispatch, trimmedQuery, isSearchable]);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }),
    [i18n.language]
  );

  const suggestions = useMemo<Suggestion[]>(() => {
    const items: Suggestion[] = [];
    const date = parseDateQuery(trimmedQuery);
    if (date) {
      items.push({
        key: "date",
        kind: "date",
        label: t("header.searchGoToDate", { date: dateFormat.format(date) }),
        to: `${ROUTES.CALENDAR}?date=${toDateParam(date)}`,
      });
    }
    if (isSearchable) {
      items.push(
        ...sessions.map<Suggestion>((session) => ({
          key: `session-${session.id}`,
          kind: "session",
          label: session.title,
          sublabel: dateFormat.format(session.dateStart),
          to: sessionDetailsRoute(session.id),
        })),
        ...members.map<Suggestion>((member) => ({
          key: `member-${member.id}`,
          kind: "member",
          label: member.fullName,
          sublabel: member.email ?? undefined,
          to: memberDetailsRoute(member.id),
        }))
      );
    }
    return items;
  }, [trimmedQuery, isSearchable, sessions, members, dateFormat, t]);

  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions.length, trimmedQuery]);

  // Close when clicking anywhere outside the search area.
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  const showDropdown = isOpen && (isSearchable || suggestions.length > 0);
  const isLoading = status === LoadingStatus.Loading;

  const selectSuggestion = (suggestion: Suggestion) => {
    navigate(suggestion.to);
    setQuery("");
    setIsOpen(false);
    dispatch(clearSearchResults());
    inputRef.current?.blur();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!showDropdown || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const active = suggestions[activeIndex] ?? suggestions[0];
      selectSuggestion(active);
    }
  };

  const clearQuery = () => {
    setQuery("");
    dispatch(clearSearchResults());
    inputRef.current?.focus();
  };

  const sectionTitles: Record<Suggestion["kind"], string> = {
    date: t("header.searchDates"),
    session: t("header.searchSessions"),
    member: t("header.searchPeople"),
  };

  return (
    <div ref={containerRef} className="search-input-wrapper relative h-[52px]">
      <div className="search-input-container">
        <Search size={20} className="shrink-0 text-grey-30 dark:text-grey-80" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          placeholder={t("header.searchPlaceholder")}
          className="search-input"
          autoComplete="off"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {query !== "" && (
          <button type="button" aria-label={t("header.searchClear")} onClick={clearQuery}>
            <X size={20} className="shrink-0 text-grey-30 dark:text-grey-80" />
          </button>
        )}
      </div>
      {showDropdown && (
        <div className="search-dropdown">
          <ul
            id={listboxId}
            role="listbox"
            className="max-h-[320px] overflow-auto pt-1"
          >
            {suggestions.map((suggestion, index) => {
              const isFirstOfKind =
                index === 0 || suggestions[index - 1].kind !== suggestion.kind;
              return (
                <li key={suggestion.key}>
                  {isFirstOfKind && (
                    <p className="text-grey-40 dark:text-grey-70 px-4 pt-2 pb-1 text-xs font-semibold tracking-wide uppercase">
                      {sectionTitles[suggestion.kind]}
                    </p>
                  )}
                  <div
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`search-result-item flex items-baseline gap-2 ${
                      index === activeIndex ? "bg-grey-95 dark:bg-[#3E334D]" : ""
                    }`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectSuggestion(suggestion);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <p className="text-grey-20 min-w-0 truncate dark:text-white">
                      {suggestion.label}
                    </p>
                    {suggestion.sublabel && (
                      <p className="text-grey-40 dark:text-grey-70 shrink-0 text-sm">
                        {suggestion.sublabel}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
            {suggestions.length === 0 && (
              <li className="text-grey-40 dark:text-grey-70 px-4 py-2 text-sm">
                {isLoading ? t("header.searchLoading") : t("header.searchNoResults")}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
